import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Typography, IconButton, Slide, Fade, LinearProgress, useMediaQuery,
} from '@mui/material';
import {
  Close, CheckCircle, DesktopWindows, IosShare, AddBox,
  BoltRounded, WifiTetheringRounded, LaunchRounded,
} from '@mui/icons-material';

import { BRAND, PRODUCT, FONT_STACK, brandButtonSx, brandGhostButtonSx, GRADIENT_GREEN } from '../../brand';
import { getState, subscribe, promptInstall, snooze } from '../../pwa';

// ============================================================================
//  InstallAppPrompt — "put MEFIMS on this machine"
// ============================================================================
//
//  Mounted once, app-wide, outside the router. It is silent until the browser
//  tells us the app is installable, then slides a card in from the bottom.
//
//  Why a card and not a modal: this is an offer, not a decision the user has
//  to make before they can work. A hospital receptionist opening the system at
//  07:00 with a queue forming should be able to ignore it entirely. It never
//  covers the primary content, never traps focus, and "Not now" means seven
//  days of silence.
//
//  The four states it moves through:
//
//    offer      → the card, with Install / Not now
//    installing → the browser's own dialog is up, or the install is running
//    done       → installed; tell them where it went and offer to close the tab
//    manual     → iOS/Safari, where no programmatic install exists and the
//                 honest thing is to show the two taps that do work

const SHEET_MAX_WIDTH = 400;

function Bullet({ icon: Icon, children }) {
  return (
    <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
      <Icon sx={{ fontSize: 17, color: BRAND.green, mt: '1px', flexShrink: 0 }} />
      <Typography sx={{ fontSize: 12.5, color: BRAND.inkMuted, lineHeight: 1.6 }}>
        {children}
      </Typography>
    </Box>
  );
}

function InstallAppPrompt() {
  const [state, setState] = useState(getState);
  const [phase, setPhase] = useState('offer');   // offer | installing | done
  const [closed, setClosed] = useState(false);
  const isNarrow = useMediaQuery('(max-width:600px)');

  useEffect(() => subscribe(setState), []);

  // The `appinstalled` event is the only reliable signal that the install
  // actually finished — `userChoice` resolving 'accepted' only means the user
  // clicked Install in the browser's dialog, not that it succeeded.
  useEffect(() => {
    const onInstalled = () => setPhase('done');
    window.addEventListener('appinstalled', onInstalled);
    return () => window.removeEventListener('appinstalled', onInstalled);
  }, []);

  // Safety net. If the user accepted but `appinstalled` never arrives — a
  // browser that does not emit it, an install the OS quietly refused — the
  // card would otherwise sit on an indeterminate progress bar forever. A
  // stuck spinner is a worse lie than an optimistic success message.
  useEffect(() => {
    if (phase !== 'installing') return undefined;
    const t = setTimeout(() => setPhase('done'), 25000);
    return () => clearTimeout(t);
  }, [phase]);

  const handleInstall = useCallback(async () => {
    setPhase('installing');
    const outcome = await promptInstall();

    if (outcome === 'accepted') {
      // Hold on 'installing'; the appinstalled listener above moves us to
      // 'done'. Chromium can take a second or two over this.
      return;
    }

    // Declined in the browser's own dialog, or the parked event went stale.
    // Either way, back off — re-offering immediately after someone has just
    // said no is nagging.
    setPhase('offer');
    snooze();
    setClosed(true);
  }, []);

  const handleNotNow = useCallback(() => {
    snooze();
    setClosed(true);
  }, []);

  // window.close() only works on windows opened by script, so for a tab the
  // user navigated to themselves this silently does nothing. That is why the
  // copy tells them where the app went rather than promising to close for
  // them — the button is a convenience where it works, not the instruction.
  const handleCloseTab = useCallback(() => {
    window.close();
    setClosed(true);
  }, []);

  // ── When to stay out of the way ───────────────────────────────────────────
  if (closed) return null;
  if (state.standalone) return null;                       // already the app
  if (phase !== 'done' && state.snoozed) return null;      // asked recently
  if (phase === 'offer' && !state.canInstall && !state.manualOnly) return null;

  const manual = state.manualOnly && phase === 'offer';

  return (
    <Slide direction="up" in mountOnEnter unmountOnExit timeout={340}>
      <Box
        role="dialog"
        aria-label={`Install ${PRODUCT.name}`}
        sx={{
          position: 'fixed',
          zIndex: 1400,               // above MUI drawers/appbars, below alerts
          fontFamily: FONT_STACK,
          // Phone: a bottom sheet spanning the width, thumb-reachable.
          // Tablet/desktop: a card tucked into the bottom-right corner.
          left:   { xs: 12, sm: 'auto' },
          right:  { xs: 12, sm: 24 },
          bottom: { xs: 12, sm: 24 },
          width:  { xs: 'auto', sm: SHEET_MAX_WIDTH },
          maxWidth: { xs: 'none', sm: SHEET_MAX_WIDTH },
          // Never taller than the viewport on a short laptop screen.
          maxHeight: 'calc(100vh - 24px)',
          overflowY: 'auto',
          background: BRAND.surface,
          borderRadius: '18px',
          border: `1px solid ${BRAND.line}`,
          boxShadow: '0 24px 64px rgba(15,31,61,.20)',
          p: { xs: 2.25, sm: 2.75 },
        }}
      >
        {/* ── Header: the mark, the ask, and a way out ─────────────────── */}
        <Box sx={{ display: 'flex', gap: 1.75, alignItems: 'flex-start', mb: 1.75 }}>
          <Box
            sx={{
              width: 46, height: 46, borderRadius: '13px', flexShrink: 0,
              background: GRADIENT_GREEN, display: 'grid', placeItems: 'center',
              boxShadow: `0 6px 18px ${BRAND.green}45`,
            }}
          >
            {phase === 'done'
              ? <CheckCircle sx={{ fontSize: 25, color: '#fff' }} />
              : <DesktopWindows sx={{ fontSize: 23, color: '#fff' }} />}
          </Box>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: 15.5, fontWeight: 800, color: BRAND.ink, lineHeight: 1.3 }}>
              {phase === 'done'
                ? `${PRODUCT.name} is installed`
                : manual
                  ? `Add ${PRODUCT.name} to your Home Screen`
                  : `Install ${PRODUCT.name} on this computer`}
            </Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '.7px', textTransform: 'uppercase', color: BRAND.green, mt: .4 }}>
              {PRODUCT.vendor}
            </Typography>
          </Box>

          {phase !== 'installing' && (
            <IconButton
              size="small"
              aria-label="Dismiss"
              onClick={phase === 'done' ? () => setClosed(true) : handleNotNow}
              sx={{ color: BRAND.inkFaint, mt: -.5, mr: -.5 }}
            >
              <Close sx={{ fontSize: 18 }} />
            </IconButton>
          )}
        </Box>

        {/* ── Body ─────────────────────────────────────────────────────── */}
        {phase === 'done' ? (
          <Fade in>
            <Box>
              <Typography sx={{ fontSize: 13, color: BRAND.inkMuted, lineHeight: 1.7, mb: 2.25 }}>
                It has been added to your desktop and Start menu, and it has opened
                in its own window. You can close this browser tab — from now on,
                open {PRODUCT.name} from the shortcut instead.
              </Typography>

              <Button
                fullWidth variant="contained" size="large"
                endIcon={<LaunchRounded />}
                onClick={handleCloseTab}
                sx={{ ...brandButtonSx, py: 1.15, fontSize: 14 }}
              >
                Close this tab
              </Button>
              <Typography sx={{ fontSize: 11, color: BRAND.inkFaint, textAlign: 'center', mt: 1.25, lineHeight: 1.5 }}>
                If the tab stays open, close it yourself — the app window is
                already running behind it.
              </Typography>
            </Box>
          </Fade>
        ) : phase === 'installing' ? (
          <Box>
            <Typography sx={{ fontSize: 13, color: BRAND.inkMuted, lineHeight: 1.7, mb: 2 }}>
              Confirm the install in the browser dialog. This takes a moment.
            </Typography>
            <LinearProgress
              sx={{
                height: 5, borderRadius: 3, background: BRAND.navySoft,
                '& .MuiLinearProgress-bar': { background: GRADIENT_GREEN, borderRadius: 3 },
              }}
            />
          </Box>
        ) : manual ? (
          <Box>
            <Typography sx={{ fontSize: 13, color: BRAND.inkMuted, lineHeight: 1.7, mb: 1.75 }}>
              Safari installs apps by hand. Two taps:
            </Typography>

            <Box sx={{ display: 'grid', gap: 1.25, mb: 2.25 }}>
              <Bullet icon={IosShare}>
                Tap <strong>Share</strong> in the Safari toolbar.
              </Bullet>
              <Bullet icon={AddBox}>
                Choose <strong>Add to Home Screen</strong>, then <strong>Add</strong>.
              </Bullet>
            </Box>

            <Button
              fullWidth variant="outlined" onClick={handleNotNow}
              sx={{ ...brandGhostButtonSx, py: 1.05, fontSize: 13.5 }}
            >
              Got it
            </Button>
          </Box>
        ) : (
          <Box>
            <Typography sx={{ fontSize: 13, color: BRAND.inkMuted, lineHeight: 1.7, mb: 1.75 }}>
              Install it and {PRODUCT.name} runs like any other program on this
              machine — no address bar, no wrong tab.
            </Typography>

            <Box sx={{ display: 'grid', gap: 1.1, mb: 2.25 }}>
              <Bullet icon={DesktopWindows}>
                A shortcut on the desktop and in the Start menu.
              </Bullet>
              <Bullet icon={BoltRounded}>
                Opens in its own window, full screen, nothing else in the way.
              </Bullet>
              <Bullet icon={WifiTetheringRounded}>
                Starts faster — the program files live on this machine.
              </Bullet>
            </Box>

            {/* Buttons stack on a phone so neither ends up a 90px-wide tap
                target, and sit side by side from tablet up. */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column-reverse', sm: 'row' },
                gap: 1.25,
              }}
            >
              <Button
                fullWidth={isNarrow}
                variant="outlined" onClick={handleNotNow}
                sx={{ ...brandGhostButtonSx, py: 1.05, fontSize: 13.5, flex: { sm: '0 0 auto' } }}
              >
                Not now
              </Button>
              <Button
                fullWidth variant="contained" onClick={handleInstall}
                sx={{ ...brandButtonSx, py: 1.05, fontSize: 13.5, flex: 1 }}
              >
                Install
              </Button>
            </Box>

            <Typography sx={{ fontSize: 11, color: BRAND.inkFaint, textAlign: 'center', mt: 1.5, lineHeight: 1.5 }}>
              Nothing is downloaded from an app store, and no data leaves this machine.
            </Typography>
          </Box>
        )}
      </Box>
    </Slide>
  );
}

export default InstallAppPrompt;
